import type { ReactNode } from 'react'
import { useLocation } from '@tanstack/react-router'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { SETTINGS_STACK, SettingsSectionSkeleton } from '../settings/settings-section'

// The metrics screens' shapes while they load (admin.metrics*.tsx), kept off the route files so the layout's pending
// UI on the eager path carries no chart code. The copy both share lives here.

/** The metrics' screens, one route each under Metrics in the tree; the window in the URL carries across them. */
export const METRICS_PAGES = [
  { key: 'overview', to: '/admin/metrics', label: 'Overview' },
  { key: 'endpoints', to: '/admin/metrics/endpoints', label: 'Endpoints' },
  { key: 'errors', to: '/admin/metrics/errors', label: 'Top errors' },
  { key: 'vitals', to: '/admin/metrics/vitals', label: 'Web vitals' },
  { key: 'assistant', to: '/admin/metrics/assistant', label: 'Assistant' },
  { key: 'platform', to: '/admin/metrics/platform', label: 'Platform' },
] as const
export type MetricsPageKey = (typeof METRICS_PAGES)[number]['key']

/** The metrics screen a pathname opens: Overview unless a sub-route names another. */
export function metricsPageOf(pathname: string): MetricsPageKey {
  const sub = pathname.split('/admin/metrics/')[1]?.split('/')[0]
  return METRICS_PAGES.find((page) => page.key !== 'overview' && page.key === sub)?.key ?? 'overview'
}

export const METRICS_RANGE_LABELS = {
  '15m': '15m',
  '1h': '1h',
  '6h': '6h',
  '24h': '24h',
  '7d': '7d',
  '30d': '30d',
  'custom': 'Custom',
} as const

/** Each section's heading, shared by the screen and its skeleton so the words never jump. */
export const METRICS_COPY = {
  health: {
    title: 'Service health',
    description:
      'Rate, errors and duration, each against the same span right before it. Drag across a chart to zoom; times are UTC.',
  },
  slowest: { title: 'Slowest endpoints', description: 'The worst p95s among those called 20 times or more.' },
  topErrors: { title: 'Top errors', description: 'The errors seen most often in the window.' },
  heatmap: {
    title: 'Latency distribution',
    description:
      'Every call by how long it took, shaded on a log scale: the band is the typical call, the cloud above it the tail.',
  },
  endpoints: {
    title: 'Endpoints',
    description:
      'Every tRPC procedure, API route and server-rendered page. Hot is p95 + (p99 − p50): consistently slow and spiky endpoints both rise to the top. The browser’s telemetry beacon (common.logClientActions) is left out here, as from every API figure; Logs keeps it.',
  },
  errors: {
    title: 'Top errors',
    description: 'Server faults, uncaught browser errors and console errors, grouped by type and message.',
  },
  vitals: {
    title: 'Web vitals',
    description: 'What real browsers measured, at the 75th percentile: a page passes when three in four visits do.',
  },
  assistant: {
    title: 'Assistant',
    description: 'Turns of the in-app assistant: how long they took, what they cost, which tools.',
  },
  platform: {
    title: 'Platform',
    description:
      'What exists right now, counted in Postgres, and who signed up, signed in and came by over the window.',
  },
} as const

export const TILE_GRID = 'grid grid-cols-2 gap-3 md:grid-cols-4'
/** A screen's row of small charts: three abreast on a laptop, stacked on a phone. */
export const CHART_ROW = 'grid grid-cols-1 gap-3 lg:grid-cols-3'

export function ToolbarSkeleton() {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Skeleton className="h-9 w-[372px] max-w-full rounded-lg" />
      <Skeleton className="h-4 w-64" />
    </div>
  )
}

export function TilesSkeleton(props: { count: number }) {
  return (
    <div className={TILE_GRID}>
      {Array.from({ length: props.count }, (_, i) => (
        <Skeleton key={i} className="h-[92px] rounded-lg" />
      ))}
    </div>
  )
}

/** One chart card: its title, its description, its legend and its plot, `plot` px tall. */
export function ChartSkeleton(props: { plot: number; className?: string }) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-3 rounded-lg border p-4', props.className)}>
      <Skeleton className="h-4 w-40" />
      <Skeleton className="h-3 w-56 max-w-full" />
      <Skeleton className="h-3 w-32" />
      <Skeleton className="w-full" style={{ height: props.plot + 20 }} />
    </div>
  )
}

export function ChartRowSkeleton(props: { count: number }) {
  return (
    <div className={CHART_ROW}>
      {Array.from({ length: props.count }, (_, i) => (
        <ChartSkeleton key={i} plot={120} />
      ))}
    </div>
  )
}

/** The heatmap: its shade key, its plot and its time axis. */
export function HeatmapSkeleton() {
  return <Skeleton className="h-[164px] w-full" />
}

export function TableSkeleton(props: { rows: number; toolbar?: boolean }) {
  return (
    <div className="flex flex-col gap-3">
      {props.toolbar && <Skeleton className="h-8 w-72 max-w-full rounded-lg" />}
      <div className="flex flex-col gap-px overflow-hidden rounded-xl border">
        {Array.from({ length: props.rows }, (_, i) => (
          <Skeleton key={i} className="h-11 rounded-none" />
        ))}
      </div>
    </div>
  )
}

export function VitalsSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} className="h-[118px] rounded-lg" />
        ))}
      </div>
      <TableSkeleton rows={6} />
    </div>
  )
}

export function AssistantSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <TilesSkeleton count={4} />
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <ChartSkeleton plot={160} />
        <Skeleton className="h-[260px] rounded-lg" />
      </div>
    </div>
  )
}

function PendingSection(props: { copy: keyof typeof METRICS_COPY; children: ReactNode }) {
  const copy = METRICS_COPY[props.copy]
  return (
    <SettingsSectionSkeleton title={copy.title} description={copy.description}>
      {props.children}
    </SettingsSectionSkeleton>
  )
}

export const SCREEN_SKELETONS: Record<MetricsPageKey, () => ReactNode> = {
  overview: () => (
    <>
      <PendingSection copy="health">
        <div className="flex flex-col gap-3">
          <TilesSkeleton count={8} />
          <ChartRowSkeleton count={3} />
        </div>
      </PendingSection>
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <PendingSection copy="slowest">
          <TableSkeleton rows={5} />
        </PendingSection>
        <PendingSection copy="topErrors">
          <TableSkeleton rows={5} />
        </PendingSection>
      </div>
    </>
  ),
  endpoints: () => (
    <>
      <PendingSection copy="heatmap">
        <HeatmapSkeleton />
      </PendingSection>
      <PendingSection copy="endpoints">
        <TableSkeleton rows={10} toolbar />
      </PendingSection>
    </>
  ),
  errors: () => (
    <>
      <PendingSection copy="errors">
        <div className="flex flex-col gap-3">
          <ChartSkeleton plot={120} />
          <TableSkeleton rows={8} />
        </div>
      </PendingSection>
    </>
  ),
  vitals: () => (
    <PendingSection copy="vitals">
      <VitalsSkeleton />
    </PendingSection>
  ),
  assistant: () => (
    <PendingSection copy="assistant">
      <AssistantSkeleton />
    </PendingSection>
  ),
  platform: () => (
    <PendingSection copy="platform">
      <div className="flex flex-col gap-3">
        <TilesSkeleton count={8} />
        <ChartRowSkeleton count={3} />
      </div>
    </PendingSection>
  ),
}

/** One screen's pending: its sections' skeletons, under the toolbar the layout already shows. */
function ScreenPending(props: { page: MetricsPageKey }) {
  const Screen = SCREEN_SKELETONS[props.page]
  return (
    <div className={SETTINGS_STACK}>
      <Screen />
    </div>
  )
}

export const OverviewPending = () => <ScreenPending page="overview" />
export const EndpointsPending = () => <ScreenPending page="endpoints" />
export const ErrorsPending = () => <ScreenPending page="errors" />
export const VitalsPending = () => <ScreenPending page="vitals" />
export const AssistantPending = () => <ScreenPending page="assistant" />
export const PlatformPending = () => <ScreenPending page="platform" />

/** The whole metrics page while its layout loads: the toolbar over the screen the URL names. */
export function MetricsPending() {
  const page = useLocation({ select: (location) => metricsPageOf(location.pathname) })
  const Screen = SCREEN_SKELETONS[page]
  return (
    <div className={SETTINGS_STACK}>
      <ToolbarSkeleton />
      <Screen />
    </div>
  )
}
